import { useState, useCallback } from "react";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { supabase } from "../supabaseClient";

// Inner form component that uses the reCAPTCHA hook
function SignupForm() {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSignup = useCallback(async (e) => {
    e.preventDefault();
    setMessage(null);

    if (password !== confirm) {
      setMessage({ type: "error", text: "Passwords do not match." });
      return;
    }

    if (password.length < 8) {
      setMessage({ type: "error", text: "Password must be at least 8 characters." });
      return;
    }

    if (code.length < 8) {
      setMessage({ type: "error", text: "Invalid invite code — must be at least 8 characters." });
      return;
    }

    setLoading(true);

    try {
      // Get reCAPTCHA token
      let captchaToken = null;
      if (executeRecaptcha) {
        captchaToken = await executeRecaptcha("signup");
      }

      const res = await fetch("/api/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, code, captchaToken }),
      });

      const data = await res.json();

      if (!res.ok) {
        setMessage({ type: "error", text: data.error });
        setLoading(false);
        return;
      }

      // Auto sign in after signup
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        setSuccess(true);
      } else {
        window.location.href = "/";
      }

    } catch (err) {
      setMessage({ type: "error", text: "Something went wrong. Please try again." });
    }

    setLoading(false);
  }, [executeRecaptcha, email, password, confirm, code]);

  if (success) {
    return (
      <div className="login-page">
        <div className="login-card">
          <div className="login-header">
            <div className="login-logo">🎉</div>
            <h1 className="login-title">Welcome to Staley Family Photos!</h1>
            <p className="login-subtitle">Your account has been created successfully.</p>
          </div>
          <a href="/" className="login-btn" style={{ display: "block", textAlign: "center", textDecoration: "none" }}>
            Go to Gallery
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">👶</div>
          <h1 className="login-title">Staley Family Photos</h1>
          <p className="login-subtitle">Create your account to view photos of Amelia!</p>
        </div>

        <form className="login-form" onSubmit={handleSignup}>
          {message && (
            <div className={message.type === "success" ? "message-success" : "error-banner"}>
              {message.type === "success" ? "✅" : "⚠️"} {message.text}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Invite Code</label>
            <input
              className="form-input invite-code-input"
              type="text"
              placeholder="Enter your invite code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              required
              minLength={8}
              maxLength={20}
            />
            <span className="form-hint">Received via text from Logan or Janet · Min 8 characters</span>
          </div>

          <div className="form-group">
            <label className="form-label">Email Address</label>
            <input
              className="form-input"
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <input
              className="form-input"
              type="password"
              placeholder="Minimum 8 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Confirm Password</label>
            <input
              className="form-input"
              type="password"
              placeholder="Re-enter your password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={8}
            />
          </div>

          <button className="login-btn" type="submit" disabled={loading}>
            {loading ? "Creating account..." : "Create Account"}
          </button>

          <p className="signup-login-link">
            Already have an account? <a href="/">Sign in here</a>
          </p>

          <p className="recaptcha-notice">
            Protected by reCAPTCHA ·{" "}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Privacy</a>
            {" "}·{" "}
            <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer">Terms</a>
          </p>
        </form>
      </div>
    </div>
  );
}

// Outer component that provides reCAPTCHA context
export default function Signup() {
  return (
    <GoogleReCaptchaProvider reCaptchaKey="6LfSgY4tAAAAAGqi781yPQlrOiD6exUCYHbnNBUg">
      <SignupForm />
    </GoogleReCaptchaProvider>
  );
}
